"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheJourneyToTheTop() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const milestones = [{
    year: "2018",
    title: "ApexTutor established",
    description: "Founded in a small study space with a focus on elite STEM mentorship.",
    position: "top"
  }, {
    year: "2019",
    title: "SAT Math mastery",
    description: "Developed proprietary strategies for perfect SAT Math scoring.",
    position: "bottom"
  }, {
    year: "2020",
    title: "Digital whiteboard launch",
    description: "Transitioned to high-fidelity remote tutoring with custom digital ink.",
    position: "top"
  }, {
    year: "2021",
    title: "AP Physics breakthrough",
    description: "Achieved 98% pass rate for students in AP Physics C: Mechanics.",
    position: "bottom"
  }, {
    year: "2022",
    title: "Calculus BC curriculum",
    description: "Launched comprehensive Calculus BC prep modules for top-tier universities.",
    position: "top"
  }, {
    year: "2023",
    title: "Elite college admissions",
    description: "Mentored students to record-breaking acceptance rates at Ivy League schools.",
    position: "bottom"
  }, {
    year: "2024",
    title: "Diagnostic tool release",
    description: "Released the 10-question diagnostic test for rapid skill assessment.",
    position: "top"
  }, {
    year: "2025",
    title: "7 years of academic ascent",
    description: "Over 1,000 students mentored, 500+ perfect scores, countless futures unlocked.",
    position: "bottom"
  }];
  return <section data-section-id="4862" id="the-journey-to-the-top" className="dark bg-background text-foreground py-24 md:py-40 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-4 mb-8">
            <span className="block h-px w-12 bg-primary" aria-hidden="true" />
            <span className="font-mono text-xs tracking-widest uppercase text-primary">
              ApexTutor / Academic Ascent
            </span>
          </div>
          <h2 className="font-default font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
            A history of <span className="italic">mastery</span>.
          </h2>
        </div>

        <div ref={lineRef} className="hidden md:flex mt-24 relative gap-12">
          <div className="flex flex-col justify-between py-4 font-mono text-[10px] text-muted-foreground/60 tracking-widest min-h-[800px]">
            <span>Y:2018</span>
            <span>Y:2020</span>
            <span>Y:2022</span>
            <span>Y:2024</span>
            <span>Y:2025</span>
          </div>

          <div className="relative w-px h-full min-h-[800px] bg-border">
            <div className="absolute top-0 left-0 w-px bg-primary transition-all duration-[1800ms] ease-out" style={{
            height: lineVisible ? "100%" : "0%"
          }} />
            <div className="absolute top-0 left-0 bottom-0 w-full">
              {milestones.map((m, i) => {
              const topPct = (i + 0.5) / milestones.length * 100;
              return <div key={i} data-index={i} className="absolute -translate-y-1/2" style={{
                top: `${topPct}%`,
                left: "-4px"
              }}>
                    <div className={`w-2 h-2 rounded-lg bg-primary transition-all duration-500 ${lineVisible ? "opacity-100 scale-100" : "opacity-0 scale-0"}`} style={{
                  transitionDelay: `${500 + i * 120}ms`
                }} />
                  </div>;
            })}
              <div className="absolute bottom-0 left-0 -translate-x-1/2 flex items-center gap-2 translate-y-8">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-lg h-3 w-3 bg-primary" />
                </span>
                <span className="font-mono text-[10px] tracking-widest text-primary">NOW</span>
              </div>
            </div>
          </div>

          <div className="relative flex flex-col space-y-16 pt-4 pb-8 flex-1">
            {milestones.map((m, i) => <div key={i} data-index={i} className="w-full">
                <div className={`transition-all duration-700 ${lineVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`} style={{
              transitionDelay: `${400 + i * 120}ms`
            }}>
                  <div className="font-mono text-xs text-primary tracking-widest mb-2">
                    {m.year}
                  </div>
                  <h3 className="font-default font-serif text-xl md:text-3xl leading-tight text-foreground mb-2">
                    {m.title}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed max-w-xl">
                    {m.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="md:hidden mt-16 relative">
          <div className="absolute left-3 top-0 bottom-0 w-px bg-border" aria-hidden="true">
            <div className="absolute top-0 left-0 w-px bg-primary transition-all duration-[1800ms] ease-out" style={{
            height: lineVisible ? "100%" : "0%"
          }} />
          </div>

          <div className="space-y-12">
            {milestones.map((m, i) => <div key={i} data-index={i} className={`relative pl-10 transition-all duration-700 ${lineVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`} style={{
            transitionDelay: `${300 + i * 100}ms`
          }}>
                <span className="absolute left-2 top-2 w-2 h-2 rounded-lg bg-primary" />
                <div className="font-mono text-xs text-primary tracking-widest mb-2">
                  {m.year}
                </div>
                <h3 className="font-default font-serif text-2xl leading-tight text-foreground mb-2">
                  {m.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {m.description}
                </p>
              </div>)}
            <div className="relative pl-10">
              <span className="absolute left-1 top-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75" />
                <span className="relative inline-flex rounded-lg h-4 w-4 bg-primary" />
              </span>
              <span className="font-mono text-xs tracking-widest text-primary">NOW</span>
            </div>
          </div>
        </div>

        <div ref={closingRef} className={`mt-24 md:mt-32 max-w-3xl mx-auto text-center transition-all duration-1000 ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default font-serif italic text-2xl md:text-3xl leading-snug text-foreground">
            Your academic ascent begins with a single, calculated step.
          </p>
        </div>
      </div>
    </section>;
}